import { useStandaloneSpeech, type StandaloneSpeech } from "./hooks/use-standalone-speech";
import {
  type AssistantTransition,
  useAssistantTransitions,
} from "./hooks/use-assistant-transitions";
import { AuthContext } from "@/features/auth/hooks/auth-context";
import {
  createContext,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  use,
  useState,
  useRef,
  useCallback,
  useLayoutEffect,
} from "react";
import { useElevenLabsAgent } from "@/features/voice-agent/hooks/use-elevenlabs-agent";
import { isLiveSessionActive } from "@/features/voice-agent/voice-session-status";
import { useDocumentProposals } from "@/features/voice-agent/assistant-document-proposals";
import { matchesApprovalCommand } from "@/features/voice-agent/approval-command";
import type {
  ApprovalChallenge,
  ProposalCreationResult,
  ReadyAssistantDocumentProposal,
} from "@/features/voice-agent/assistant-document-proposals";

/**
 * The shell owns the typed draft alongside the session because the compact
 * composer lives in a popover that unmounts on close: a draft held locally
 * would be discarded the moment the operator dismisses the launcher.
 */
export type VoiceAssistantValue = ReturnType<typeof useElevenLabsAgent> & {
  draft: string;
  setDraft: Dispatch<SetStateAction<string>>;
  actorId?: string | null;
  speech?: StandaloneSpeech;
  documentProposals?: ReturnType<typeof useDocumentProposals>;
  approveByVoice?: (
    proposal: ReadyAssistantDocumentProposal,
    challenge: ApprovalChallenge,
    onSubmitting: () => void,
  ) => Promise<ProposalCreationResult>;
  voiceApprovalAvailable?: boolean;
  stopVoiceApproval?: () => void;
  cancelVoiceApproval?: () => void;
};

interface VoiceApprovalContext {
  documentProposals: ReturnType<typeof useDocumentProposals>;
  latestSession: { current: ReturnType<typeof useElevenLabsAgent> };
  session: ReturnType<typeof useElevenLabsAgent>;
  speech: StandaloneSpeech;
  voiceApprovalGeneration: { current: number };
}

function microphoneTrack(
  session: ReturnType<typeof useElevenLabsAgent>,
  live: boolean,
): MediaStreamTrack | undefined {
  return live ? session.microphone?.getAudioTracks()[0] : undefined;
}

function isLiveSourceReady(
  live: boolean,
  source: MediaStreamTrack | undefined,
  status: string,
): boolean {
  if (!live) return true;
  return Boolean(source) && ["listening", "speaking"].includes(status);
}

/** The cloned stream is never passed to ElevenLabs; it stays local to the capture. */
function cloneEnabledTrack(source: MediaStreamTrack | undefined): MediaStreamTrack | undefined {
  const cloned = source?.clone();
  if (cloned) cloned.enabled = true;
  return cloned;
}

function muteRestoreFlags(
  session: ReturnType<typeof useElevenLabsAgent>,
  live: boolean,
): { restoreInput: boolean; restoreOutput: boolean } {
  return {
    restoreInput: live && !session.muted,
    restoreOutput: live && !session.outputMuted,
  };
}

function isSessionStillLive(live: boolean, current: { mode?: string; status: string }): boolean {
  if (!live) return true;
  return current.mode === "live" && ["listening", "speaking"].includes(current.status);
}

function captureRequest(
  proposal: ReadyAssistantDocumentProposal,
  challenge: ApprovalChallenge,
  cloned: MediaStreamTrack | undefined,
) {
  return {
    challenge: challenge.challenge,
    conversationId: proposal.conversationId,
    proposalId: proposal.proposalId,
    revision: proposal.revision,
    ...(cloned ? { capture: async () => new MediaStream([cloned]) } : {}),
  };
}

/**
 * Isolated from OwnedAssistant so its recording/approval-window bookkeeping is
 * its own reviewable unit rather than inflating the provider component's
 * complexity. Reads the live refs at call time; mutates nothing but the
 * provider mute/microphone state it explicitly restores.
 */
async function performVoiceApproval(
  context: VoiceApprovalContext,
  proposal: ReadyAssistantDocumentProposal,
  challenge: ApprovalChallenge,
  onSubmitting: () => void,
): Promise<ProposalCreationResult> {
  const { documentProposals, latestSession, session, speech, voiceApprovalGeneration } = context;
  const generation = voiceApprovalGeneration.current;
  if (!speech.capabilities.approvalCapture.available) {
    throw new Error("Voice confirmation is unavailable.");
  }
  const live = session.mode === "live";
  const source = microphoneTrack(session, live);
  if (!isLiveSourceReady(live, source, session.status)) {
    throw new Error("The voice session is not ready for confirmation.");
  }
  const cloned = cloneEnabledTrack(source);
  const { restoreInput, restoreOutput } = muteRestoreFlags(session, live);
  if (restoreInput) session.toggleMute();
  if (restoreOutput) session.toggleOutputMute();
  try {
    const capture = await speech.startApprovalCapture(captureRequest(proposal, challenge, cloned));
    const current = latestSession.current;
    if (!isSessionStillLive(live, current)) {
      throw new Error("The voice session changed during confirmation.");
    }
    if (!matchesApprovalCommand(capture.transcript, challenge.approvalCommand)) {
      throw new Error("Voice confirmation did not match the displayed command.");
    }
    if (generation !== voiceApprovalGeneration.current) {
      throw new Error("Voice confirmation was canceled.");
    }
    onSubmitting();
    return await documentProposals.approve({ item: proposal, challenge: challenge.challenge });
  } finally {
    if (restoreOutput) session.toggleOutputMute();
    if (restoreInput) session.toggleMute();
  }
}

const VoiceAssistantContext = createContext<VoiceAssistantValue | null>(null);

export function VoiceAssistantProvider({ children }: { children: ReactNode }) {
  const auth = use(AuthContext);
  const actorId = auth?.user?.id ?? null;
  return (
    <OwnedAssistant key={actorId ?? "anonymous"} actorId={actorId}>
      {children}
    </OwnedAssistant>
  );
}

function OwnedAssistant({ children, actorId }: { children: ReactNode; actorId: string | null }) {
  const session = useElevenLabsAgent(actorId);
  const latestSession = useRef(session);
  useLayoutEffect(() => {
    latestSession.current = session;
  }, [session]);
  const documentProposals = useDocumentProposals(session.conversationId ?? null);
  const [draft, setDraft] = useState("");
  const transitionRef = useRef<AssistantTransition>(null);
  const appendDraft = useCallback(
    (text: string) =>
      setDraft((current) => (current.trimEnd() ? `${current.trimEnd()} ${text}` : text)),
    [],
  );
  const speech = useStandaloneSpeech({
    actorId,
    mode: session.mode ?? "text",
    textPending: Boolean(session.textPending),
    conversationId: session.textConversationId ?? null,
    transitioning: () => transitionRef.current !== null,
    appendDraft,
    turns: session.turns,
  });
  const voiceApprovalGeneration = useRef(0);
  const cancelVoiceApproval = useCallback(() => {
    voiceApprovalGeneration.current++;
    speech.cancelApprovalCapture();
  }, [speech]);
  const approveByVoice = useCallback(
    (
      proposal: ReadyAssistantDocumentProposal,
      challenge: ApprovalChallenge,
      onSubmitting: () => void,
    ) =>
      performVoiceApproval(
        { documentProposals, latestSession, session, speech, voiceApprovalGeneration },
        proposal,
        challenge,
        onSubmitting,
      ),
    [documentProposals, session, speech],
  );
  const { start, stop } = useAssistantTransitions({ session, speech, transitionRef });
  // A thread switch waits out dictation or a start/stop, and silences read-aloud.
  const switchThread = (change?: () => Promise<boolean>) => {
    if (!change || speech.isDictating() || transitionRef.current !== null)
      return Promise.resolve(false);
    speech.stopReadAloud(true);
    return change();
  };
  const value: VoiceAssistantValue = {
    ...session,
    draft,
    setDraft,
    actorId,
    speech,
    documentProposals,
    approveByVoice,
    voiceApprovalAvailable: speech.capabilities.approvalCapture.available,
    stopVoiceApproval: speech.stopDictation,
    cancelVoiceApproval,
    start,
    stop,
    toggle: () => {
      void (isLiveSessionActive(session.status) ? stop() : start());
    },
    resumeThread: (threadId, turns) =>
      switchThread(session.resumeThread && (() => session.resumeThread!(threadId, turns))),
    newThread: () => switchThread(session.newThread),
    sendText: (text) =>
      speech.isDictating() || transitionRef.current !== null
        ? Promise.resolve(false)
        : session.sendText(text),
  };
  return <VoiceAssistantContext.Provider value={value}>{children}</VoiceAssistantContext.Provider>;
}

export function useVoiceAssistant(): VoiceAssistantValue {
  const session = use(VoiceAssistantContext);
  if (!session) {
    throw new Error("useVoiceAssistant must be used inside VoiceAssistantProvider");
  }
  return session;
}
